import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, CircleCheck, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { PageHeader } from 'components/ui/page-header';
import { SegmentedTabs, TableSearch } from 'components/ui/data-table';
import { Skeleton } from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import Avatar from 'components/ui/avatar';
import { toast } from 'components/ui/use-toast';
import { GradingExportButton } from 'components/result/GradingExportButton';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import * as Grading from 'modules/grading';
import * as TestModule from 'modules/test';
import type { AiGradeSuggestion } from 'modules/grading/api';
import { IeltsBandGrader } from 'modules/standardizedExam/components/grading/IeltsBandGrader';
import config from 'config';
import { cn } from 'lib/utils';
import {
  AiSuggestionBox,
  GraderNote,
  GradingRubric,
  GradingStatus,
  answerLabel,
  answerToText,
  buildQueueItems,
  groupQueue,
  questionNumberOf,
  questionNumbers,
  questionTypeLabelKey,
  type GradingMode,
  type RubricCriterion,
} from './grading-parts';

export interface GradingSplitPaneProps {
  /** Scope the queue to one test (per-test Reviews); omit for the global inbox. */
  testId?: string;
}

/** AI is assistive (Accept / Adjust — never auto-applied) and on-demand, not stored per respondent. */
export const GradingSplitPane: React.FC<GradingSplitPaneProps> = ({ testId }) => {
  const { t, i18n } = useTranslation();
  const { state: authState } = useAuthContext();
  const canAiGrading = useFeature(FEATURES.AI_GRADING);
  const openUpgrade = useOpenUpgradeForFeature();
  const orgId =
    (typeof localStorage !== 'undefined' ? localStorage.getItem(config.api.active_org_key) : null) ||
    authState.organization?.id ||
    undefined;

  // GRADING_VIEW holders can open the queue read-only; only GRADING_GRADE can
  // score, AI-suggest, or save. Hide those affordances for view-only reviewers
  // (the server enforces this too — this just avoids showing 403-ing controls).
  const canGrade = usePermission(PERMISSIONS.GRADING_GRADE);

  const { data: pending = [], isLoading } = Grading.usePendingGrading(orgId);
  const gradeManual = Grading.useGradeManual();
  const aiSuggest = Grading.useAiSuggest();

  const [mode, setMode] = useState<GradingMode>('question');
  const [search, setSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [activeAnswerId, setActiveAnswerId] = useState<string | null>(null);
  const [awarded, setAwarded] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [aiScore, setAiScore] = useState<number | null>(null);
  const [suggestion, setSuggestion] = useState<AiGradeSuggestion | null>(null);
  const [graded, setGraded] = useState<Set<string>>(new Set());

  // Flatten the pending queue → per-answer items (optionally scoped to a test).
  const items = useMemo(() => buildQueueItems(pending, t, testId), [pending, testId, t]);
  const numbers = useMemo(() => questionNumbers(items), [items]);
  const groups = useMemo(() => groupQueue(items, mode, numbers), [items, mode, numbers]);
  const numberOf = useMemo(
    () => new Map(items.map((item, index) => [item.answerId, questionNumberOf(numbers, item, index)])),
    [items, numbers],
  );

  const currentGroup = groups.find((g) => g.key === activeGroup) ?? groups[0];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = currentGroup?.items ?? [];
    return q ? list.filter((r) => r.candidateName.toLowerCase().includes(q)) : list;
  }, [currentGroup, search]);

  const activeItem = (currentGroup?.items ?? []).find((r) => r.answerId === activeAnswerId) ?? rows[0] ?? null;
  const activeNumber = activeItem ? numberOf.get(activeItem.answerId) ?? 0 : 0;

  // Rubric from the active answer's test grading config.
  const { item: activeTest } = TestModule.Hooks.useSingle({
    id: activeItem?.testId || '',
    enabled: Boolean(activeItem?.testId),
  });
  const rubric = useMemo<RubricCriterion[]>(() => {
    const fb = (activeTest as any)?.config?.grading?.feedback;
    const r = fb?.rubric;
    return Array.isArray(r) ? r : [];
  }, [activeTest]);

  // Reset or pre-fill the scoring state whenever the selected answer changes.
  useEffect(() => {
    setAwarded({});
    setNotes('');
    if (activeItem?.aiScore != null && activeItem?.aiReasoning) {
      setSuggestion({
        suggestion: true,
        answerId: activeItem.answerId,
        questionId: activeItem.questionId,
        maxPoints: activeItem.maxPoints,
        suggestedScore: activeItem.aiScore,
        confidence: activeItem.aiConfidence,
        rationale: activeItem.aiReasoning,
      });
      setAwarded({ __single: activeItem.aiScore });
      setAiScore(activeItem.aiScore);
      setNotes(activeItem.aiReasoning);
    } else {
      setAiScore(null);
      setSuggestion(null);
    }
  }, [
    activeItem?.answerId,
    activeItem?.aiScore,
    activeItem?.aiConfidence,
    activeItem?.aiReasoning,
    activeItem?.questionId,
    activeItem?.maxPoints,
  ]);

  const usingRubric = rubric.length > 0 && aiScore === null;
  const rubricTotal = rubric.reduce((s, c) => s + (awarded[c.id] ?? 0), 0);
  const singleScore = awarded['__single'] ?? 0;
  const maxPoints = activeItem?.maxPoints ?? 1;
  const rawTotal = aiScore !== null ? aiScore : usingRubric ? rubricTotal : singleScore;
  // Clamp so a rubric whose criteria sum beyond the question's points can't post
  // a score the server rejects with a 400.
  const total = Math.min(rawTotal, maxPoints);

  const step = (id: string, delta: number, max: number) =>
    setAwarded((prev) => {
      setAiScore(null);
      const next = Math.max(0, Math.min(max, (prev[id] ?? 0) + delta));
      return { ...prev, [id]: next };
    });

  const handleSuggest = useCallback(async () => {
    if (!activeItem) return;
    // AI grading is a paid capability (Growth+). Open the upgrade dialog instead
    // of firing a request that the backend FeatureGuard would 403.
    if (!canAiGrading) {
      openUpgrade(FEATURES.AI_GRADING);
      return;
    }
    try {
      const { data } = await aiSuggest.mutateAsync({
        attemptId: activeItem.attemptId,
        questionId: activeItem.questionId,
        language: i18n.language,
      });
      setSuggestion(data);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      toast.error(code === 'AI_GRADING_DISABLED' ? t('ai_grading_disabled') : t('ai_suggestion_failed'));
    }
  }, [activeItem, aiSuggest, t, i18n.language, canAiGrading, openUpgrade]);

  // Accept: take the AI score as-is. Reflect it in the VISIBLE score control
  // (not just the hidden total) so the click has an obvious effect — even when
  // the suggested score is 0 — and mark it AI-accepted for the "matches AI" cue.
  const acceptAi = () => {
    if (!suggestion) return;
    setAwarded((prev) => ({ ...prev, __single: suggestion.suggestedScore }));
    setAiScore(suggestion.suggestedScore);
  };

  // Adjust: seed the manual score from the suggestion and hand control back to
  // the grader (steppers) instead of snapping to zero, so they tweak from the
  // AI's number.
  const adjustAi = () => {
    if (!suggestion) return;
    setAwarded((prev) => ({ ...prev, __single: suggestion.suggestedScore }));
    // Without a rubric, clear aiScore so the single stepper (seeded above) takes
    // over and the grader tweaks from the AI number. WITH a rubric there is no
    // single stepper — the total comes from per-criterion awards (all 0) — so
    // clearing aiScore would snap the total to 0 and silently discard the AI
    // score. Keep it applied until the grader edits a criterion (which hands off
    // to the rubric via step()).
    setAiScore(rubric.length > 0 ? suggestion.suggestedScore : null);
  };

  const goToItem = useCallback(
    (dir: 1 | -1) => {
      if (!currentGroup) return;
      const list = currentGroup.items;
      const idx = list.findIndex((r) => r.answerId === activeItem?.answerId);
      const next = list[idx + dir];
      if (next) setActiveAnswerId(next.answerId);
    },
    [currentGroup, activeItem],
  );

  const handleSaveNext = async () => {
    if (!activeItem) return;
    try {
      await gradeManual.mutateAsync({
        answerId: activeItem.answerId,
        score: total,
        notes: notes.trim() || undefined,
      });
      setGraded((g) => new Set(g).add(activeItem.answerId));
      goToItem(1);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('ai_suggestion_failed'));
    }
  };

  // J / K keyboard flow between respondents.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key === 'j' || e.key === 'J') goToItem(1);
      if (e.key === 'k' || e.key === 'K') goToItem(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goToItem]);

  // Progress is this session's: the pending endpoint returns only what is still
  // pending, so answers graded before the page opened are not counted. A graded
  // answer leaves the queue once the pending query refetches; until then it is
  // counted as graded, never twice.
  const gradedCount = graded.size;
  const remaining = items.filter((item) => !graded.has(item.answerId)).length;
  const sessionTotal = remaining + gradedCount;
  const progress = sessionTotal > 0 ? Math.round((gradedCount / sessionTotal) * 100) : 0;
  const matchesAi = suggestion !== null && aiScore !== null && aiScore === suggestion.suggestedScore;
  const keysHint = t('grading_keys_hint');

  const renderQueue = () => (
    <aside className="flex max-h-[45vh] min-h-0 flex-col border-b border-border lg:max-h-none lg:border-b-0 lg:border-r">
      {/* Groups: questions (S1, S2 …) or respondents, with how many answers each holds */}
      <div className="flex flex-wrap gap-1.5 border-b border-border px-3.5 py-3">
        {groups.map((g) => {
          const active = g.key === (currentGroup?.key ?? '');
          const first = g.items[0];
          const label = mode === 'question' ? t('grading_question_short', { n: g.questionNumber }) : g.name;
          const description =
            mode === 'question' && first
              ? `${t('grading_question_n', { n: g.questionNumber })} · ${t(questionTypeLabelKey(first.questionType))}`
              : g.name;
          return (
            <button
              key={g.key}
              type="button"
              aria-pressed={active}
              title={description}
              onClick={() => {
                setActiveGroup(g.key);
                setActiveAnswerId(g.items[0]?.answerId ?? null);
              }}
              className={cn(
                'inline-flex h-7 max-w-[180px] items-center gap-1.5 rounded-full px-2.5 text-[12.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'bg-foreground text-background'
                  : 'border border-border bg-card text-foreground/80 hover:border-foreground/20 hover:text-foreground',
              )}
            >
              <span className="truncate">{label}</span>
              <span className="tabular-nums opacity-70">{g.items.length}</span>
            </button>
          );
        })}
      </div>
      <div className="border-b border-border p-3">
        {/* Constrain to the 300px sidebar — TableSearch defaults to sm:w-72. */}
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder={t('grading_find_respondent')}
          aria-label={t('grading_find_respondent')}
          className="sm:w-full"
        />
      </div>
      <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
        {rows.map((r) => {
          const active = r.answerId === activeItem?.answerId;
          const number = numberOf.get(r.answerId) ?? 0;
          return (
            <li key={r.answerId}>
              <button
                type="button"
                data-grading-row={r.answerId}
                aria-current={active ? 'true' : undefined}
                onClick={() => setActiveAnswerId(r.answerId)}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  active ? 'bg-muted' : 'hover:bg-background',
                )}
              >
                {mode === 'question' ? (
                  <>
                    <Avatar name={r.candidateName} size="xs" className="h-7 w-7 text-[11px]" />
                    <span className={cn('min-w-0 flex-1 truncate text-[13.5px] text-foreground', active ? 'font-semibold' : 'font-medium')}>
                      {r.candidateName}
                    </span>
                  </>
                ) : (
                  <span className={cn('min-w-0 flex-1 truncate text-[13.5px] text-foreground', active ? 'font-semibold' : 'font-medium')}>
                    {`${t('grading_question_n', { n: number })} · ${t(questionTypeLabelKey(r.questionType))}`}
                  </span>
                )}
                <GradingStatus graded={graded.has(r.answerId)} />
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );

  const renderDetail = () => {
    if (!activeItem) {
      return <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">{t('no_pending_grading')}</div>;
    }
    if ((activeItem.writingTask || activeItem.questionType === 'SPEAKING') && canGrade) {
      // IELTS-style Writing / Speaking: criterion bands instead of points.
      return (
        <IeltsBandGrader
          attemptId={activeItem.attemptId}
          questionId={activeItem.questionId}
          answerId={activeItem.answerId}
          task={activeItem.writingTask ?? 'SPEAKING'}
          recordings={activeItem.speakingRecordings}
          aiAvailable={activeItem.aiAvailable}
          script={answerToText(activeItem.value)}
          suggested={activeItem.aiBandCriteria}
          suggestedFeedback={activeItem.aiReasoning}
          canUseAi={canAiGrading}
          onUpgrade={() => openUpgrade(FEATURES.AI_GRADING)}
          onSkip={() => goToItem(1)}
          onGraded={() => {
            setGraded((g) => new Set(g).add(activeItem.answerId));
            goToItem(1);
          }}
        />
      );
    }
    const meta = [
      t('grading_question_n', { n: activeNumber }),
      t(questionTypeLabelKey(activeItem.questionType)),
      t('n_points', { count: maxPoints }),
      activeItem.testTitle,
    ].filter(Boolean);
    return (
      <>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="space-y-1.5">
            <p data-grading-meta className="text-[12.5px] text-muted-foreground">
              {meta.join(' · ')}
            </p>
            <p className="text-base font-medium leading-normal text-foreground">{activeItem.questionText}</p>
          </div>

          <div className="rounded-lg border border-border bg-background px-4 py-3.5">
            <p className="text-xs font-medium text-muted-foreground">{answerLabel(activeItem, t)}</p>
            <p className="mt-1.5 whitespace-pre-wrap text-[14.5px] leading-relaxed text-foreground">{answerToText(activeItem.value)}</p>
          </div>

          {canGrade && (
            <>
              <AiSuggestionBox
                suggestion={suggestion}
                matchesAi={matchesAi}
                requesting={aiSuggest.isPending}
                canUseAi={canAiGrading}
                onRequest={handleSuggest}
                onAccept={acceptAi}
                onAdjust={adjustAi}
              />
              <GradingRubric criteria={rubric} awarded={awarded} maxPoints={maxPoints} onStep={step} />
              <GraderNote id="grading-note" value={notes} onChange={setNotes} />
            </>
          )}
        </div>

        {/* Bottom bar — grading actions, GRADING_GRADE only. */}
        {canGrade && (
          <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-t border-border bg-card px-6 py-3">
            <span data-grading-total className="text-[15px] font-bold tabular-nums text-foreground">
              {t('grading_total')} {total} / {maxPoints}
            </span>
            {matchesAi && <span className="text-[12.5px] text-muted-foreground">{t('grading_matches_ai')}</span>}
            <span className="ml-auto hidden items-center lg:inline-flex" title={keysHint}>
              <kbd aria-hidden className="font-mono text-[11.5px] text-muted-foreground">
                J / K
              </kbd>
              <span className="sr-only">{keysHint}</span>
            </span>
            <Button variant="outline" size="sm" className="ml-auto lg:ml-0" onClick={() => goToItem(1)}>
              {t('grading_skip')}
            </Button>
            <Button size="sm" className="gap-1.5" onClick={handleSaveNext} disabled={gradeManual.isPending}>
              {gradeManual.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {t('grading_save_next')}
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderLoading = () => (
    <div data-grading-loading className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[300px_minmax(0,1fr)]">
      <div className="border-b border-border lg:border-b-0 lg:border-r">
        <div className="flex gap-1.5 border-b border-border px-3.5 py-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-7 w-12 rounded-full" />
          ))}
        </div>
        <div className="divide-y divide-border">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-2.5 px-3.5 py-2.5">
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton className={cn('h-3.5 flex-1', i % 2 ? 'max-w-[45%]' : 'max-w-[60%]')} />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-4 px-6 py-5">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-5 w-2/3" />
        <Skeleton variant="rounded" className="h-24 w-full" />
        <Skeleton variant="rounded" className="h-20 w-full" />
      </div>
    </div>
  );

  // The page fills the shell (or the test workspace) height from lg up; the
  // queue and the answer scroll on their own below the header and summary.
  return (
    <div id="grading-page-container" data-tour="grading-page" data-grading-page className="flex flex-col gap-4 lg:h-full lg:min-h-0">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            {testId ? t('grade_responses') : t('nav.grading')}
            {!isLoading && remaining > 0 && (
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                {remaining}
              </span>
            )}
          </span>
        }
        actions={
          <>
            <SegmentedTabs<GradingMode>
              aria-label={t('grading_queue')}
              options={[
                { value: 'question', label: t('grading_by_question') },
                { value: 'respondent', label: t('grading_by_respondent') },
              ]}
              value={mode}
              onChange={setMode}
            />
            {/* Export is org-scoped (all graded answers), so only on the global inbox. */}
            {!testId && <GradingExportButton orgId={orgId} />}
          </>
        }
      />

      {!isLoading && sessionTotal > 0 && (
        <div data-grading-progress className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
          <span className="tabular-nums">
            <span className="font-semibold text-foreground">{gradedCount}</span> / {sessionTotal} {t('grading_session_graded')}
          </span>
          <span
            role="progressbar"
            aria-label={t('grading_session_graded')}
            aria-valuemin={0}
            aria-valuemax={sessionTotal}
            aria-valuenow={gradedCount}
            className="h-1.5 w-40 overflow-hidden rounded-full bg-muted"
          >
            <span className="block h-full rounded-full bg-foreground transition-[width] duration-300" style={{ width: `${progress}%` }} />
          </span>
          <span className="tabular-nums">
            <span className="font-semibold text-warning-700 dark:text-warning-400">{remaining}</span> {t('grading_pending_suffix')}
          </span>
        </div>
      )}

      <section className="flex min-h-[420px] flex-col overflow-hidden rounded-lg border border-border bg-card lg:min-h-0 lg:flex-1">
        {isLoading ? (
          renderLoading()
        ) : items.length === 0 ? (
          <EmptyState icon={<CircleCheck />} title={t('no_pending_grading')} className="flex-1" />
        ) : (
          /* A fixed 300px queue beside the detail leaves ~90px for the answer on a phone, so stack until there's room. */
          <div className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[300px_minmax(0,1fr)]">
            {renderQueue()}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">{renderDetail()}</div>
          </div>
        )}
      </section>
    </div>
  );
};

export default GradingSplitPane;
